import React, { useState, useEffect, useCallback } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Select, { SelectOption } from '@amzn/meridian/select';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import { getDSData, callApi } from '../services/api';
import { API_STAGE, STAGE, getCurrentUser } from '../appConfig';
import { downloadCSV } from '../utils/csvUtils';

const CAP_RELEASE_CONFIGURATIONS_TABLE = `fortaleza-${STAGE}-cap-release-configurations`;

// --- Configuration Type Options ---
const CONFIGURATION_TYPE_OPTIONS = [
    'Cap Modifier',
    'Cap Split',
    'Capacity Constraint',
    'Increment Modifier',
    'MVC Penalty',
];

const NODE_TYPE_OPTIONS = ['Long Zone', 'In Zone'];
const CAP_TYPE_OPTIONS = ['HARD', 'SOFT'];
const DEPLOYMENT_TYPE_OPTIONS_ALL = ['CORE', 'TACTICAL', 'UPLIFT'];
const DEPLOYMENT_TYPE_OPTIONS_DEFAULT = ['CORE'];
const SPLIT_TYPE_OPTIONS = ['Split', 'Adjustment', 'Value'];

// --- Field definitions ---
interface FieldDef {
    key: string;
    label: string;
    type?: 'select';
    options?: string[];
}

const BASE_FIELDS: FieldDef[] = [
    { key: 'level', label: 'Level' },
    { key: 'cycle_name', label: 'Cycle Name' },
    { key: 'sort_code', label: 'Sort Code' },
    { key: 'cit', label: 'CIT' },
    { key: 'horizon', label: 'Horizon' },
    { key: 'node_type', label: 'Node Type', type: 'select', options: NODE_TYPE_OPTIONS },
    { key: 'flexibility', label: 'Flexibility' },
    { key: 'business_line', label: 'Business Line' },
    { key: 'deployment_type', label: 'Deployment Type', type: 'select', options: DEPLOYMENT_TYPE_OPTIONS_DEFAULT },
    { key: 'round', label: 'Round' },
];


interface ConfigRecord {
    id: string;
    level: string;
    cycle_name: string;
    sort_code: string;
    cit: string;
    horizon: string;
    node_type: string;
    flexibility: string;
    business_line: string;
    deployment_type: string;
    round: string;
    configuration_type: string;
    cap_type: string;
    ship_method: string;
    ship_option_group_name: string;
    split_type: string;
    low: string;
    high: string;
    value: string;
    [key: string]: string;
}

const EMPTY_RECORD: ConfigRecord = {
    id: '',
    level: '',
    cycle_name: '',
    sort_code: '',
    cit: '',
    horizon: '',
    node_type: '',
    flexibility: '',
    business_line: 'AMZL',
    deployment_type: 'CORE',
    round: '',
    configuration_type: '',
    cap_type: '',
    ship_method: '',
    ship_option_group_name: '',
    split_type: '',
    low: '',
    high: '',
    value: '',
};

const PAGE_SIZE = 50;

const CapReleaseConfig: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [data, setData] = useState<ConfigRecord[]>([]);
    const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error'; content: string } | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [editValues, setEditValues] = useState<ConfigRecord>({ ...EMPTY_RECORD });
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [configurationType, setConfigurationType] = useState<string>('');
    const [isUploading, setIsUploading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterText, setFilterText] = useState('');

    const filteredData = (() => {
        if (!filterText.trim()) return data;
        const lowerFilter = filterText.toLowerCase();
        return data.filter(item =>
            Object.values(item).some(val =>
                String(val).toLowerCase().includes(lowerFilter)
            )
        );
    })();

    const paginatedItems = (() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        return filteredData.slice(startIndex, startIndex + PAGE_SIZE);
    })();

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE) || 1;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await getDSData(CAP_RELEASE_CONFIGURATIONS_TABLE, {});
            const items = Array.isArray(result?.items || result) ? (result?.items || result) : [];
            const sorted = items
                .map((item: any) => ({
                    id: item.id || '',
                    level: item.level || '',
                    cycle_name: item.cycle_name || '',
                    sort_code: item.sort_code || '',
                    cit: item.cit || '',
                    horizon: item.horizon || '',
                    node_type: item.node_type || '',
                    flexibility: item.flexibility || '',
                    business_line: item.business_line || '',
                    deployment_type: item.deployment_type || '',
                    round: item.round || '',
                    configuration_type: item.configuration_type || '',
                    cap_type: item.cap_type || '',
                    ship_method: item.ship_method || '',
                    ship_option_group_name: item.ship_option_group_name || '',
                    split_type: item.split_type || '',
                    service_type: item.service_type || '',
                    low: item.low || '',
                    high: item.high || '',
                    value: item.value || '',
                }))
                .sort((a: ConfigRecord, b: ConfigRecord) => parseInt(a.id) - parseInt(b.id));
            setData(sorted);
        } catch (error: any) {
            setAlertMessage({ type: 'error', content: `Failed to load data: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const getModalFields = (): FieldDef[] => {
        const fields = [...BASE_FIELDS];
        if (configurationType === 'Cap Modifier') {
            // Override deployment_type options for Cap Modifier
            const deploymentIndex = fields.findIndex(f => f.key === 'deployment_type');
            if (deploymentIndex !== -1) {
                fields[deploymentIndex] = { ...fields[deploymentIndex], options: DEPLOYMENT_TYPE_OPTIONS_ALL };
            }
            const sortCodeIndex = fields.findIndex(f => f.key === 'sort_code');
            fields.splice(sortCodeIndex + 1, 0, { key: 'ship_method', label: 'Ship Method' });
            // Ship Option Group set (TACTICAL): generates SOG records in tactical CIMS instead of modifying base caps
            // (SOFT = Value % of base cap, HARD = hard cap modifier)
            fields.push({ key: 'ship_option_group_name', label: 'Ship Option Group' });
            fields.push({ key: 'cap_type', label: 'Cap Type', type: 'select', options: CAP_TYPE_OPTIONS });
            fields.push({ key: 'value', label: 'Value' });
        } else if (configurationType === 'Cap Split') {
            // Insert ship_method after sort_code
            const sortCodeIndex = fields.findIndex(f => f.key === 'sort_code');
            fields.splice(sortCodeIndex + 1, 0, { key: 'ship_method', label: 'Ship Method' });
            fields.push({ key: 'cap_type', label: 'Cap Type', type: 'select', options: CAP_TYPE_OPTIONS });
            fields.push({ key: 'split_type', label: 'Split Type', type: 'select', options: SPLIT_TYPE_OPTIONS });
            fields.push({ key: 'value', label: 'Value' });
        } else if (configurationType === 'Increment Modifier') {
            return [
                { key: 'business_line', label: 'Business Line' },
                { key: 'deployment_type', label: 'Deployment Type', type: 'select' as const, options: DEPLOYMENT_TYPE_OPTIONS_ALL },
                { key: 'horizon', label: 'Horizon' },
                { key: 'round', label: 'Round' },
                { key: 'node_type', label: 'Node Type', type: 'select' as const, options: NODE_TYPE_OPTIONS },
                { key: 'level', label: 'Level' },
                { key: 'cycle_name', label: 'Cycle Name' },
                { key: 'service_type', label: 'Service Type' },
                { key: 'value', label: 'Value' },
            ];
        } else if (configurationType === 'Capacity Constraint' || configurationType === 'MVC Penalty') {
            fields.push({ key: 'value', label: 'Value' });
        }
        return fields;
    };

    const handleAddRow = () => {
        const newId = String(data.length > 0 ? Math.max(...data.map(d => parseInt(d.id) || 0)) + 1 : 1);
        setEditValues({ ...EMPTY_RECORD, id: newId });
        setEditingIndex(null);
        setModalMode('add');
        setConfigurationType('');
        setModalVisible(true);
    };

    const handleEditRow = (item: ConfigRecord) => {
        const index = data.findIndex(d => d.id === item.id);
        setEditValues({ ...item });
        setEditingIndex(index);
        setModalMode('edit');
        setConfigurationType(item.configuration_type || '');
        setModalVisible(true);
    };

    const handleCopyRow = (item: ConfigRecord) => {
        const newId = String(data.length > 0 ? Math.max(...data.map(d => parseInt(d.id) || 0)) + 1 : 1);
        setEditValues({ ...item, id: newId });
        setEditingIndex(null);
        setModalMode('add');
        setConfigurationType(item.configuration_type || '');
        setModalVisible(true);
    };

    const handleDeleteRow = async (item: ConfigRecord) => {
        if (!confirm(`Are you sure you want to delete record ID: ${item.id}?`)) return;
        try {
            await callApi({
                method: 'delete_item',
                apiRoute: `${API_STAGE}/config`,
                table_name: CAP_RELEASE_CONFIGURATIONS_TABLE,
                key: { id: item.id },
            });
            setData(prev => prev.filter(d => d.id !== item.id));
            setAlertMessage({ type: 'success', content: `Record ${item.id} deleted successfully` });
        } catch (error: any) {
            setAlertMessage({ type: 'error', content: `Failed to delete record: ${error.message}` });
        }
    };

    const handleModalDismiss = () => {
        setModalVisible(false);
        setEditValues({ ...EMPTY_RECORD });
        setEditingIndex(null);
        setConfigurationType('');
    };

    const handleInputChange = (field: string, value: string) => {
        setEditValues(prev => ({ ...prev, [field]: value }));
    };

    const handleConfigurationTypeChange = (value: string) => {
        setConfigurationType(value);
        setEditValues(prev => ({ ...prev, configuration_type: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const record = { ...editValues };
            const user = getCurrentUser();
            await callApi({
                method: 'update_config',
                apiRoute: `${API_STAGE}/config`,
                table_name: CAP_RELEASE_CONFIGURATIONS_TABLE,
                items: [record],
                key_columns: ['id'],
                user,
            });

            if (modalMode === 'edit' && editingIndex !== null) {
                const updated = [...data];
                updated[editingIndex] = record;
                setData(updated);
            } else {
                setData([...data, record]);
            }
            setModalVisible(false);
            setEditValues({ ...EMPTY_RECORD });
            setEditingIndex(null);
            setConfigurationType('');
            setAlertMessage({ type: 'success', content: 'Record saved successfully' });
        } catch (error: any) {
            setAlertMessage({ type: 'error', content: `Failed to save: ${error.message}` });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownload = () => {
        if (!data.length) return;
        const headers = ['id', 'business_line', 'deployment_type', 'horizon', 'round', 'node_type', 'flexibility', 'level', 'cycle_name', 'sort_code', 'cit','service_type', 'ship_method', 'ship_option_group_name', 'configuration_type', 'cap_type', 'split_type', 'low', 'high', 'value'];
        const downloadData = filterText.trim() ? filteredData : data;
        const rows = downloadData.map(item => headers.map(h => String(item[h] || '')));
        downloadCSV(`cap-release-configurations-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
    };

    const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        event.target.value = '';

        if (!file.name.endsWith('.csv')) {
            setAlertMessage({ type: 'error', content: 'Please upload a valid CSV file.' });
            return;
        }

        setIsUploading(true);
        try {
            // Read file as base64 for upload
            const fileContent = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    // Extract base64 content from data URL
                    const base64 = result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsDataURL(file);
            });

            const user = getCurrentUser();
            await callApi({
                method: 'upload_cap_release_config',
                apiRoute: `${API_STAGE}/config`,
                file_content: fileContent,
                uploaded_by: user,
            });

            setAlertMessage({ type: 'success', content: 'Successfully uploaded cap release config from CSV.' });
            fetchData();
        } catch (error: any) {
            setAlertMessage({ type: 'error', content: `Failed to upload CSV: ${error.message}` });
        } finally {
            setIsUploading(false);
        }
    };

    if (isLoading) {
        return (
            <Column alignmentHorizontal="center" spacingInset="500">
                <Loader size="large" />
                <Text>Loading Cap Release Configurations...</Text>
            </Column>
        );
    }

    return (
        <Column spacing="400" spacingInset="400">
            {alertMessage && (
                <Alert
                    type={alertMessage.type}
                    onClose={() => setAlertMessage(null)}
                >
                    {alertMessage.content}
                </Alert>
            )}

            {/* Header + Actions */}
            <Row alignmentHorizontal="justify" alignmentVertical="center">
                <Text type="h300">Cap Release Configurations ({filteredData.length})</Text>
                <Row spacing="300">
                    <Button type="tertiary" size="small" onClick={fetchData}>Refresh</Button>
                    <Button type="tertiary" size="small" onClick={handleDownload} disabled={!data.length}>Download CSV</Button>
                    <Button
                        type="tertiary"
                        size="small"
                        onClick={() => document.getElementById('csv-upload-input')?.click()}
                        disabled={isUploading}
                    >
                        {isUploading ? 'Uploading...' : 'Upload CSV'}
                    </Button>
                    <Button type="primary" size="small" onClick={handleAddRow}>Add Row</Button>
                </Row>
            </Row>

            {/* Search */}
            <Input
                value={filterText}
                onChange={(value) => { setFilterText(value); setCurrentPage(1); }}
                placeholder="Search configurations..."
            />

            {/* Table */}
            <Table headerRows={1} showDividers spacing="small">
                <TableRow highlightOnHover={false}>
                    <TableCell><Text type="b200" color="secondary">ID</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Business</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Deployment</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Horizon</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Round</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Node Type</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Level</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Cycle</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Service Type</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Sort Code</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Ship Method</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">SOG</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">CIT</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Config Type</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Cap Type</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Split Type</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Low</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">High</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Value</Text></TableCell>
                    <TableCell><Text type="b200" color="secondary">Actions</Text></TableCell>
                </TableRow>
                {paginatedItems.map((item) => (
                    <TableRow key={item.id} highlightOnHover>
                        <TableCell><Text type="b200">{item.id}</Text></TableCell>
                        <TableCell><Text type="b200">{item.business_line}</Text></TableCell>
                        <TableCell><Text type="b200">{item.deployment_type}</Text></TableCell>
                        <TableCell><Text type="b200">{item.horizon}</Text></TableCell>
                        <TableCell><Text type="b200">{item.round}</Text></TableCell>
                        <TableCell><Text type="b200">{item.node_type}</Text></TableCell>
                        <TableCell><Text type="b200">{item.level}</Text></TableCell>
                        <TableCell><Text type="b200">{item.cycle_name}</Text></TableCell>
                        <TableCell><Text type="b200">{item.service_type}</Text></TableCell>
                        <TableCell><Text type="b200">{item.sort_code}</Text></TableCell>
                        <TableCell><Text type="b200">{item.ship_method}</Text></TableCell>
                        <TableCell><Text type="b200">{item.ship_option_group_name}</Text></TableCell>
                        <TableCell><Text type="b200">{item.cit}</Text></TableCell>
                        <TableCell><Text type="b200">{item.configuration_type}</Text></TableCell>
                        <TableCell><Text type="b200">{item.cap_type}</Text></TableCell>
                        <TableCell><Text type="b200">{item.split_type}</Text></TableCell>
                        <TableCell><Text type="b200">{item.low}</Text></TableCell>
                        <TableCell><Text type="b200">{item.high}</Text></TableCell>
                        <TableCell><Text type="b200">{item.value}</Text></TableCell>
                        <TableCell>
                            <Row spacing="200">
                                <Button size="small" type="tertiary" onClick={() => handleEditRow(item)}>Edit</Button>
                                <Button size="small" type="tertiary" onClick={() => handleCopyRow(item)}>Copy</Button>
                                <Button size="small" type="tertiary" onClick={() => handleDeleteRow(item)}>Delete</Button>
                            </Row>
                        </TableCell>
                    </TableRow>
                ))}
            </Table>

            {filteredData.length === 0 && (
                <Column alignmentHorizontal="center" spacingInset="400">
                    <Text type="b300" color="secondary">
                        {filterText ? 'No records match the search criteria.' : 'No configurations. Click "Add Row" to create one.'}
                    </Text>
                </Column>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Row alignmentHorizontal="center" spacing="200">
                    <Button size="small" type="tertiary" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Previous</Button>
                    <Text type="b200">Page {currentPage} of {totalPages}</Text>
                    <Button size="small" type="tertiary" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Next</Button>
                </Row>
            )}

            {/* Hidden file input for CSV upload */}
            <input
                type="file"
                id="csv-upload-input"
                style={{ display: 'none' }}
                accept=".csv"
                onChange={handleCsvUpload}
            />

            {/* Add/Edit Modal */}
            <Modal
                title={modalMode === 'edit' ? `Edit Record (ID: ${editValues.id})` : 'Add New Record'}
                open={modalVisible}
                onClose={handleModalDismiss}
                scrollContainer="viewport"
            >
                <Column spacing="400">
                    <Column spacing="200">
                        <Text type="b200" color="secondary">Configuration Type</Text>
                        <Select
                            value={configurationType}
                            onChange={(value) => handleConfigurationTypeChange(value as string)}
                            placeholder="Select Configuration Type"
                        >
                            {CONFIGURATION_TYPE_OPTIONS.map(opt => (
                                <SelectOption key={opt} value={opt} label={opt} />
                            ))}
                        </Select>
                    </Column>

                    {configurationType && (
                        <Row wrap="down" spacing="300">
                            {getModalFields().map(field => (
                                <Column key={field.key} spacing="100" width={200}>
                                    <Text type="b200" color="secondary">{field.label}</Text>
                                    {field.type === 'select' ? (
                                        <Select
                                            value={editValues[field.key] || ''}
                                            onChange={(value) => handleInputChange(field.key, value as string)}
                                            placeholder={`Select ${field.label}`}
                                        >
                                            {(field.options || []).map(opt => (
                                                <SelectOption key={opt} value={opt} label={opt} />
                                            ))}
                                        </Select>
                                    ) : (
                                        <Input
                                            value={editValues[field.key] || ''}
                                            onChange={(value) => handleInputChange(field.key, value)}
                                            placeholder={`Enter ${field.label}`}
                                        />
                                    )}
                                </Column>
                            ))}
                        </Row>
                    )}
                </Column>
                <ModalFooter>
                    <Row alignmentHorizontal="end" spacing="300">
                        <Button type="tertiary" onClick={handleModalDismiss}>Cancel</Button>
                        <Button type="primary" onClick={handleSave} disabled={isSaving || !configurationType}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Column>
    );
};

export default CapReleaseConfig;
